import { useState, useEffect } from "react";
import "./recordPayment.css";
import dataService from "../services/dataService";
import { useNavigate, useParams } from "react-router-dom";
import SearchableSelect from "../components/SearchableSelect";
import { useToast } from "../context/ToastContext";

function RecordPayment() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [customers, setCustomers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [purchases, setPurchases] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [custRes, vendRes, invRes, purRes] = await Promise.all([
          dataService.getCustomers(),
          dataService.getVendors(),
          dataService.getOrders("Invoice"),
          dataService.getPurchases()
        ]);
        setCustomers(Array.isArray(custRes.data.data) ? custRes.data.data : []);
        setVendors(Array.isArray(vendRes.data.data) ? vendRes.data.data : []);
        setInvoices(Array.isArray(invRes.data.data) ? invRes.data.data : []);
        setPurchases(Array.isArray(purRes.data.data) ? purRes.data.data : []);
      } catch (err) {
        console.error("Failed to fetch data for payments", err);
      }
    };
    fetchData();
  }, []);

  const [formData, setFormData] = useState({
    paymentId: `PAY-${Math.floor(1000 + Math.random() * 9000)}`,
    invoice: "",
    customer: "",
    date: new Date().toISOString().split('T')[0],
    amount: 0,
    method: "Bank Transfer",
    referenceNumber: "",
    notes: "",
    currency: "AED",
    receivedBy: "",
    depositToAccount: "Business checking",
    type: "Receive Payment",
    status: "Completed",
    bankDetails: {
      bankName: "",
      accountNumber: "",
      iban: "",
      swiftCode: ""
    }
  });

  useEffect(() => {
    if (isEditMode) {
      const fetchPayment = async () => {
        try {
          const response = await dataService.getPayments();
          const payment = response.data.data.find(p => p._id === id);
          if (payment) {
            setFormData({
              paymentId: payment.paymentId || "",
              invoice: payment.invoice || "",
              customer: payment.customer || "",
              date: payment.date ? new Date(payment.date).toISOString().split('T')[0] : "",
              amount: payment.amount || 0,
              method: payment.method || "Bank Transfer",
              referenceNumber: payment.referenceNumber || "",
              notes: payment.notes || "",
              currency: payment.currency || "AED",
              receivedBy: payment.receivedBy || "",
              depositToAccount: payment.depositToAccount || "Business checking",
              type: payment.type || "Receive Payment",
              status: payment.status || "Completed",
              bankDetails: payment.bankDetails || { bankName: "", accountNumber: "", iban: "", swiftCode: "" }
            });
          } else {
            setError("Payment record not found");
          }
        } catch (err) {
          setError("Failed to fetch payment details");
        } finally {
          // done
        }
      };
      fetchPayment();
    }
  }, [id, isEditMode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("bank")) {
      let field = name.replace("bank", "");
      field = field.charAt(0).toLowerCase() + field.slice(1);
      if (field === "account") field = "accountNumber";
      if (field === "routing") field = "iban";
      if (field === "swift") field = "swiftCode";

      setFormData(prev => ({
        ...prev,
        bankDetails: { ...prev.bankDetails, [field]: value }
      }));
    } else {
      setFormData(prev => {
        const updated = {
          ...prev,
          [name]: (name === "amount") ? parseFloat(value) || 0 : value
        };

        // Reset customer/invoice if type changes
        if (name === "type") {
          updated.customer = "";
          updated.invoice = "";
          updated.amount = 0;
        }

        // Auto-fill amount if invoice/purchase is selected
        if (name === "invoice") {
          if (updated.type === "Receive Payment") {
            const selectedInv = invoices.find(inv => inv.orderId === value);
            if (selectedInv) updated.amount = selectedInv.amount;
          } else {
            const selectedPur = purchases.find(pur => pur.purchaseId === value);
            if (selectedPur) updated.amount = selectedPur.amount;
          }
        }

        return updated;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      if (isEditMode) {
        await dataService.updatePayment(id, formData);
        addToast("Payment record updated successfully", "success");
      } else {
        await dataService.recordPayment(formData);
        addToast("Payment recorded successfully", "success");
      }
      navigate("/payments");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to record payment");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-vendor-page">
      <div className="page-header">
        <h1>{isEditMode ? "Edit Payment" : "Record Payment"}</h1>
        <p>{isEditMode ? `Updating transaction ${formData.paymentId}` : "Record a new payment transaction"}</p>
      </div>

      {error && <div className="error-message" style={{ color: 'red', marginBottom: '20px' }}>{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="vendor-grid">
          <div className="vendor-left">
            <div className="vendor-card">
              <h2>Payment Information</h2>
              <div className="v-name">
                <div>
                  <p>Payment ID</p>
                  <input name="paymentId" value={formData.paymentId} onChange={handleChange} required placeholder="PAY-xxx" />
                </div>
                <div>
                  <p>Payment Type</p>
                  <select name="type" value={formData.type} onChange={handleChange}>
                    <option value="Receive Payment">Receive Payment (from customer)</option>
                    <option value="Send Payment">Send Payment (to vendor)</option>
                  </select>
                </div>
              </div>

              <div className="v-name">
                <div>
                  <p>Invoice/Reference #</p>
                  <select name="invoice" value={formData.invoice} onChange={handleChange} required>
                    <option value="">Select Reference</option>
                    {formData.type === "Receive Payment" ? (
                      invoices
                        .filter(inv => inv.customer === formData.customer && inv.status !== "Paid")
                        .map(inv => (
                          <option key={inv._id} value={inv.orderId}>{inv.orderId} - AED {inv.amount}</option>
                        ))
                    ) : (
                      purchases
                        .filter(pur => pur.vendor === formData.customer && pur.status !== "Paid")
                        .map(pur => (
                          <option key={pur._id} value={pur.purchaseId}>{pur.purchaseId} - AED {pur.amount}</option>
                        ))
                    )}
                  </select>
                </div>
                <div>
                  <SearchableSelect
                    label={formData.type === "Receive Payment" ? "Customer" : "Vendor"}
                    name="customer"
                    options={formData.type === "Receive Payment" ? customers : vendors}
                    value={formData.customer}
                    onChange={handleChange}
                    placeholder={`Search/Select a ${formData.type === "Receive Payment" ? "Customer" : "Vendor"}`}
                    addPath={formData.type === "Receive Payment" ? "/customers/add" : "/vendors/add"}
                    addLabel={`+ New ${formData.type === "Receive Payment" ? "Customer" : "Vendor"}`}
                  />
                </div>
              </div>

              <div className="v-name">
                <div>
                  <p>Payment Date</p>
                  <input type="date" name="date" value={formData.date} onChange={handleChange} required />
                </div>
                <div>
                  <p>Amount Paid</p>
                  <input type="number" name="amount" value={formData.amount} onChange={handleChange} required placeholder="0.00" />
                </div>
              </div>

              <div className="v-name">
                <div>
                  <p>Payment Method</p>
                  <select name="method" value={formData.method} onChange={handleChange}>
                    <option value="Cash">Cash</option>
                    <option value="Bank Transfer">Bank Transfer</option>
                    <option value="Check">Check</option>
                    <option value="Credit Card">Credit Card</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <p>Reference (Check/Trans ID)</p>
                  <input name="referenceNumber" value={formData.referenceNumber} onChange={handleChange} placeholder="TRX-xxxx-xxxxx" />
                </div>
              </div>
            </div>

            <div className="vendor-card">
              <h2>Bank Details (Optional)</h2>
              <div className="v-name">
                <div>
                  <p>Bank Name</p>
                  <input name="bankName" value={formData.bankDetails.bankName} onChange={handleChange} placeholder="Enter Bank Name" />
                </div>
                <div>
                  <p>Account Number</p>
                  <input name="bankAccount" value={formData.bankDetails.accountNumber} onChange={handleChange} placeholder="XXXX-XXXX-XXXX" />
                </div>
              </div>

              <div className="v-name">
                <div>
                  <p>IBAN</p>
                  <input name="bankRouting" value={formData.bankDetails.iban} onChange={handleChange} placeholder="GBXX XXXX XXXX XXXX XXXX XX" />
                </div>
                <div>
                  <p>Swift Code</p>
                  <input name="bankSwift" value={formData.bankDetails.swiftCode} onChange={handleChange} placeholder="XXXX-XXXX-XXXX" />
                </div>
              </div>
            </div>
          </div>

          <div className="vendor-right">
            <div className="vendor-card">
              <div className="v-namer">
                <h2>Transaction Status</h2>
                <div>
                  <p>Status</p>
                  <select name="status" value={formData.status} onChange={handleChange}>
                    <option value="Completed">Completed</option>
                    <option value="Pending">Pending</option>
                    <option value="Failed">Failed</option>
                  </select>
                  <p>Currency</p>
                  <select name="currency" value={formData.currency} onChange={handleChange}>
                    <option value="AED">AED - United Arab Emirates Dirham</option>
                    <option value="USD">USD - US Dollar</option>
                    <option value="EUR">EUR - Euro</option>
                    <option value="GBP">GBP - British Pound</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="vendor-card">
              <div className="v-namer">
                <h2>Internal tracking</h2>
                <div>
                  <p>Received/Paid By</p>
                  <input name="receivedBy" value={formData.receivedBy} onChange={handleChange} placeholder="Employee name" />
                  <p>Deposit/Withdraw from</p>
                  <select name="depositToAccount" value={formData.depositToAccount} onChange={handleChange}>
                    <option value="Business checking">Business checking</option>
                    <option value="Business saving">Business saving</option>
                    <option value="Petty Cash">Petty Cash</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="vendor-card">
              <h2>Notes</h2>
              <textarea name="notes" value={formData.notes} onChange={handleChange} placeholder="Internal notes or payment details" rows={5} style={{ width: '100%' }}></textarea>
            </div>

            <div className="vendor-actions">
              <button type="button" className="secondary-btn" onClick={() => navigate("/payments")}>Cancel</button>
              <button type="submit" className="primary-btn" disabled={loading}>
                {loading ? "Saving..." : (isEditMode ? "Update Payment" : "Record Payment")}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default RecordPayment;
